import React, { useState } from "react";
import { useNavigate } from "react-router";
import { useFormik } from "formik";
import { usePaketeValidation } from "../../Components/Validation/use_Pakete";
import { useToast } from "../../Hooks/useToast";
import { FetchCreatePackeges } from "../../Apis/Package/Package";
import imageCompression from "browser-image-compression";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";

import FormikAutocompleteChips from "../../Components/Utils/Formik_Autocomplete";
import AlternativeHeader from "../../Components/Headres/Alternative_Header";
import FromikTextField from "../../Components/Utils/Formik_Texfiled";
import CustomButton from "../../Components/Utils/Custom_Btn";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const VisuallyHiddenInput = (props) => (
  <input style={{ display: "none" }} {...props} />
);

const AddEditPakete = () => {
  const durationOptions = [
    { label: "30 Minuten", value: 30 },
    { label: "1 Stunde", value: 60 },
    { label: "1.30 Stunden", value: 90 },
    { label: "2 Stunden", value: 120 },
    { label: "2.30 Stunden", value: 150 },
    { label: "3 Stunden", value: 180 },
    { label: "3.30 Stunden", value: 210 },
    { label: "4 Stunden", value: 240 },
    { label: "4.30 Stunden", value: 270 },
    { label: "5 Stunden", value: 300 },
    { label: "5.30 Stunden", value: 330 },
    { label: "6 Stunden", value: 360 }
  ];
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const { toastSuccess, toastError } = useToast();

  const navigate = useNavigate();
  const { formValidation } = usePaketeValidation();

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
        if (file) {
          try {
            const options = {
              maxSizeMB: 1,
              maxWidthOrHeight: 1024,
              useWebWorker: true,
            };
    
            const compressedFile = await imageCompression(file, options);
            setSelectedImage(compressedFile);
            setImagePreview(URL.createObjectURL(compressedFile));
          } catch (error) {
            toastError("Bildkomprimierung fehlgeschlagen.");
          }
        }
      };

  const formik = useFormik({
    initialValues: {
      name: "",
      duration: "",
      price: "",
      servicesList: [],
      longDescription: ""
    },
    validationSchema: formValidation,
    onSubmit: async (values) => {
      try {
        setLoading(true);

        // التحقق من وجود صورة
        if (!selectedImage) {
          toastError("Bitte laden Sie ein Bild hoch.");
          setLoading(false);
          return;
        }

        const formData = new FormData();
        formData.append("name", values.name);
        formData.append("duration", values.duration);
        formData.append("price", values.price);
        // unified pricing: only single price is sent
        formData.append("longDescription", values.longDescription);

        // إضافة الخدمات
        values.servicesList.forEach((service, index) => {
          formData.append(`servicesList[${index}]`, service);
        });

        // إضافة الصورة
        formData.append("image", selectedImage);

        console.log("Sending package data:", {
          name: values.name,
          duration: values.duration,
          price: values.price,
          servicesList: values.servicesList,
          hasImage: !!selectedImage
        });

        const result = await FetchCreatePackeges(formData);
        console.log("API Response:", result);

        if (result?.status === 201) {
          toastSuccess("Das Paket wurde erfolgreich erzeugt!");
          navigate("/Pakete");
        } else {
          toastError(`Die Erstellung des Pakets ist fehlgeschlagen. Status: ${result?.status}`);
        }
      } catch (error) {
        console.error("Error creating package:", error);
        toastError("Während der Übermittlung ist ein Fehler aufgetreten.");
      } finally {
        setLoading(false);
      }
    }
  });

  return (
    <Box ml={2} component="form" onSubmit={formik.handleSubmit}>
      <AlternativeHeader
        onPrevious={() => navigate("/Pakete")}
        title="Neues Paket erstellen"
      />

      <Box 
        mt={6} 
        sx={{ 
          maxWidth: 700, 
          mx: 'auto',
          backgroundColor: '#ffffff',
          borderRadius: '20px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
          p: 4,
          border: '1px solid #f0f0f0'
        }}
      >
        <Grid container spacing={4}>
          {/* اسم الباكج */}
          <Grid item xs={12}>
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ color: '#2c3e50', fontWeight: 600, mb: 1 }}>
                📦 Paketname
              </Typography>
              <FromikTextField
                formik={formik}
                name="name"
                placeholder="Geben Sie den Paketnamen ein..."
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: '#f8f9fa',
                    '&:hover': {
                      backgroundColor: '#ffffff',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#ffffff',
                    }
                  }
                }}
              />
            </Box>
          </Grid>

          {/* المدة */}
          <Grid item xs={12}>
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ color: '#2c3e50', fontWeight: 600, mb: 1 }}>
                ⏰ Dauer
              </Typography>
              <FromikTextField
                formik={formik}
                name="duration"
                select
                fullWidth
                placeholder="Wählen Sie die Dauer..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: '#f8f9fa',
                    '&:hover': {
                      backgroundColor: '#ffffff',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#ffffff',
                    }
                  }
                }}
              >
                {durationOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </FromikTextField>
            </Box>
          </Grid>

          {/* السعر العادي */}
          <Grid item xs={12}>
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ color: '#2c3e50', fontWeight: 600, mb: 1 }}>
                💰 Preis (€)
              </Typography>
              <FromikTextField
                formik={formik}
                name="price"
                placeholder="z. B. 99.99"
                type="number"
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: '#f8f9fa',
                    '&:hover': {
                      backgroundColor: '#ffffff',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#ffffff',
                    }
                  }
                }}
              />
            </Box>
          </Grid>

          {/* سعر اليوم الحر */}
          {/* unified pricing: removed separate holiday price field */}

          {/* الخدمات */}
          <Grid item xs={12}>
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ color: '#2c3e50', fontWeight: 600, mb: 1 }}>
                🛎️ Services
              </Typography>
              <FormikAutocompleteChips
                formik={formik}
                name="servicesList"
                placeholder="Service hinzufügen..."
                fullWidth
              />
            </Box>
          </Grid>

          {/* رفع الصورة */}
          <Grid item xs={12}>
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ color: '#2c3e50', fontWeight: 600, mb: 2 }}>
                🖼️ Paket Bild
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  height: "250px",
                  border: imagePreview ? "3px solid #D6B981" : "3px dashed #D6B981",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                  borderRadius: "20px",
                  mb: 3,
                  backgroundColor: imagePreview ? "#ffffff" : "#fefefe",
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  '&:hover': {
                    borderColor: "#C5A572",
                    backgroundColor: "#ffffff",
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 25px rgba(214, 185, 129, 0.3)"
                  }
                }}
              >
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="preview"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "17px"
                    }}
                  />
                ) : (
                  <Box sx={{ textAlign: "center", p: 3 }}>
                    <CloudUploadIcon sx={{ 
                      fontSize: 60, 
                      color: "#D6B981", 
                      mb: 2,
                      animation: "bounce 2s infinite"
                    }} />
                    <Typography variant="h6" sx={{ color: "#2c3e50", fontWeight: 600, mb: 1 }}>
                      Bild hochladen
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Klicken Sie hier oder ziehen Sie ein Bild hinein
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                      Unterstützte Formate: JPG, PNG, GIF
                    </Typography>
                  </Box>
                )}
              </Box>

              <Button
                component="label"
                variant="contained"
                fullWidth
                size="large"
                sx={{ 
                  backgroundColor: "#D6B981",
                  borderRadius: "12px",
                  py: 1.5,
                  fontSize: "16px",
                  fontWeight: 600,
                  textTransform: "none",
                  boxShadow: "0 4px 15px rgba(214, 185, 129, 0.4)",
                  "&:hover": {
                    backgroundColor: "#C5A572",
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 20px rgba(214, 185, 129, 0.6)"
                  },
                  transition: "all 0.3s ease"
                }}
                startIcon={<CloudUploadIcon />}
              >
                {imagePreview ? "Bild ändern" : "Bild auswählen"}
                <VisuallyHiddenInput
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                />
              </Button>
            </Box>
          </Grid>

          {/* الوصف */}
          <Grid item xs={12}>
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ color: '#2c3e50', fontWeight: 600, mb: 1 }}>
                📝 Beschreibung
              </Typography>
              <FromikTextField
                formik={formik}
                name="longDescription"
                placeholder="Beschreiben Sie Ihr Paket im Detail..."
                multiline
                rows={5}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: '#f8f9fa',
                    '&:hover': {
                      backgroundColor: '#ffffff',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#ffffff',
                    }
                  }
                }}
              />
            </Box>
          </Grid>
        </Grid>

        <style>
          {`
            @keyframes bounce {
              0%, 20%, 50%, 80%, 100% {
                transform: translateY(0);
              }
              40% {
                transform: translateY(-10px);
              }
              60% {
                transform: translateY(-5px);
              }
            }
          `}
        </style>
      </Box>

      <Divider sx={{ mt: 4, bgcolor: "#000000" }} />

      <Box mt={2} display="flex" justifyContent="flex-end">
        <CustomButton
          onNavigate={formik.handleSubmit}
          title="speichern"
          disabled={loading}
        />
      </Box>
    </Box>
  );
};

export default AddEditPakete;
